import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FaTimes, FaSave, FaTruck } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './ProveedorFormModal.css';
import {
  CREAR_PROVEEDOR,
  ACTUALIZAR_PROVEEDOR,
  GET_PROVEEDORES
} from '../../graphql/proveedores';

const INITIAL_FORM = {
  nombre: '',
  nombre_comercial: '',
  nit: '',
  digito_verificacion: '',
  tipo_persona: 'juridica',
  tipo_documento: 'NIT',
  contacto_nombre: '',
  contacto_telefono: '',
  contacto_email: '',
  direccion: '',
  ciudad: '',
  condiciones_pago: 'contado',
  dias_credito: '0',
  descuento_pronto_pago: '0',
  categoria: '',
  notas: ''
};

const CATEGORIAS_PROVEEDOR = [
  'Alimentos y Bebidas',
  'Limpieza y Aseo',
  'Mantenimiento',
  'Lenceria y Amenities',
  'Tecnologia',
  'Servicios Generales',
  'Papeleria y Oficina',
  'Otro'
];

// Usage:
// <ProveedorFormModal isOpen={open} onClose={() => setOpen(false)} proveedor={null} />
// <ProveedorFormModal isOpen={open} onClose={() => setOpen(false)} proveedor={proveedorObj} />

const ProveedorFormModal = ({ isOpen, onClose, proveedor = null }) => {
  const isEditing = !!proveedor;
  const [formData, setFormData] = useState(INITIAL_FORM);

  const [crearProveedor, { loading: creando }] = useMutation(CREAR_PROVEEDOR, {
    refetchQueries: [{ query: GET_PROVEEDORES }]
  });

  const [actualizarProveedor, { loading: actualizando }] = useMutation(ACTUALIZAR_PROVEEDOR, {
    refetchQueries: [{ query: GET_PROVEEDORES }]
  });

  useEffect(() => {
    if (proveedor) {
      setFormData({
        nombre: proveedor.nombre || '',
        nombre_comercial: proveedor.nombre_comercial || '',
        nit: proveedor.nit || '',
        digito_verificacion: proveedor.digito_verificacion || '',
        tipo_persona: proveedor.tipo_persona || 'juridica',
        tipo_documento: proveedor.tipo_documento || 'NIT',
        contacto_nombre: proveedor.contacto_nombre || '',
        contacto_telefono: proveedor.contacto_telefono || '',
        contacto_email: proveedor.contacto_email || '',
        direccion: proveedor.direccion || '',
        ciudad: proveedor.ciudad || '',
        condiciones_pago: proveedor.condiciones_pago || 'contado',
        dias_credito: proveedor.dias_credito?.toString() || '0',
        descuento_pronto_pago: proveedor.descuento_pronto_pago?.toString() || '0',
        categoria: proveedor.categoria || '',
        notas: proveedor.notas || ''
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [proveedor, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre del proveedor es obligatorio');
      return;
    }

    try {
      const input = {
        nombre: formData.nombre.trim(),
        nombre_comercial: formData.nombre_comercial.trim() || null,
        nit: formData.nit.trim() || null,
        digito_verificacion: formData.digito_verificacion.trim() || null,
        tipo_persona: formData.tipo_persona,
        tipo_documento: formData.tipo_documento,
        contacto_nombre: formData.contacto_nombre.trim() || null,
        contacto_telefono: formData.contacto_telefono.trim() || null,
        contacto_email: formData.contacto_email.trim() || null,
        direccion: formData.direccion.trim() || null,
        ciudad: formData.ciudad.trim() || null,
        condiciones_pago: formData.condiciones_pago,
        dias_credito: parseInt(formData.dias_credito) || 0,
        descuento_pronto_pago: parseFloat(formData.descuento_pronto_pago) || 0,
        categoria: formData.categoria || null,
        notas: formData.notas.trim() || null
      };

      if (isEditing) {
        await actualizarProveedor({ variables: { id: parseInt(proveedor.id), input } });
        toast.success('Proveedor actualizado correctamente');
      } else {
        await crearProveedor({ variables: { input } });
        toast.success('Proveedor creado correctamente');
      }

      onClose();
    } catch (error) {
      console.error('Error al guardar proveedor:', error);
      toast.error(error.message || 'Error al guardar el proveedor');
    }
  };

  if (!isOpen) return null;

  const loading = creando || actualizando;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="proveedor-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="proveedor-form-header">
          <h2>
            <FaTruck />
            {isEditing ? 'Editar Proveedor' : 'Nuevo Proveedor'}
          </h2>
          <button className="close-button" onClick={onClose} type="button" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>

        <form className="proveedor-form" onSubmit={handleSubmit} noValidate>
          {/* Informacion Legal */}
          <div className="form-section">
            <h3>Informacion Legal</h3>

            <div className="form-row">
              <div className="form-group form-group--wide">
                <label htmlFor="pf-nombre">Razon Social / Nombre *</label>
                <input
                  id="pf-nombre"
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  placeholder="Ej: Distribuidora Nacional S.A.S."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="pf-nombre-comercial">Nombre Comercial</label>
                <input
                  id="pf-nombre-comercial"
                  type="text"
                  value={formData.nombre_comercial}
                  onChange={(e) => handleChange('nombre_comercial', e.target.value)}
                  placeholder="Nombre con el que opera"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pf-tipo-persona">Tipo de Persona *</label>
                <select
                  id="pf-tipo-persona"
                  value={formData.tipo_persona}
                  onChange={(e) => handleChange('tipo_persona', e.target.value)}
                >
                  <option value="natural">Natural</option>
                  <option value="juridica">Juridica</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pf-tipo-documento">Tipo de Documento *</label>
                <select
                  id="pf-tipo-documento"
                  value={formData.tipo_documento}
                  onChange={(e) => handleChange('tipo_documento', e.target.value)}
                >
                  <option value="NIT">NIT</option>
                  <option value="CC">Cedula de Ciudadania</option>
                  <option value="CE">Cedula de Extranjeria</option>
                  <option value="Pasaporte">Pasaporte</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pf-nit">
                  {formData.tipo_documento === 'NIT' ? 'NIT' : 'Numero de Documento'}
                </label>
                <input
                  id="pf-nit"
                  type="text"
                  value={formData.nit}
                  onChange={(e) => handleChange('nit', e.target.value)}
                  placeholder="Ej: 900123456"
                />
              </div>

              {formData.tipo_documento === 'NIT' && (
                <div className="form-group form-group--small">
                  <label htmlFor="pf-dv">Digito Verif.</label>
                  <input
                    id="pf-dv"
                    type="text"
                    value={formData.digito_verificacion}
                    onChange={(e) => handleChange('digito_verificacion', e.target.value)}
                    placeholder="0"
                    maxLength={1}
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pf-categoria">Categoria</label>
                <select
                  id="pf-categoria"
                  value={formData.categoria}
                  onChange={(e) => handleChange('categoria', e.target.value)}
                >
                  <option value="">Sin categoria</option>
                  {CATEGORIAS_PROVEEDOR.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pf-ciudad">Ciudad</label>
                <input
                  id="pf-ciudad"
                  type="text"
                  value={formData.ciudad}
                  onChange={(e) => handleChange('ciudad', e.target.value)}
                  placeholder="Ej: Bogota"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pf-direccion">Direccion</label>
              <input
                id="pf-direccion"
                type="text"
                value={formData.direccion}
                onChange={(e) => handleChange('direccion', e.target.value)}
                placeholder="Direccion completa del proveedor"
              />
            </div>
          </div>

          {/* Informacion de Contacto */}
          <div className="form-section">
            <h3>Contacto</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pf-contacto-nombre">Nombre del Contacto</label>
                <input
                  id="pf-contacto-nombre"
                  type="text"
                  value={formData.contacto_nombre}
                  onChange={(e) => handleChange('contacto_nombre', e.target.value)}
                  placeholder="Persona de contacto"
                />
              </div>

              <div className="form-group">
                <label htmlFor="pf-contacto-telefono">Telefono</label>
                <input
                  id="pf-contacto-telefono"
                  type="tel"
                  value={formData.contacto_telefono}
                  onChange={(e) => handleChange('contacto_telefono', e.target.value)}
                  placeholder="Ej: 3001234567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="pf-contacto-email">Correo Electronico</label>
                <input
                  id="pf-contacto-email"
                  type="email"
                  value={formData.contacto_email}
                  onChange={(e) => handleChange('contacto_email', e.target.value)}
                  placeholder="contacto@proveedor.com"
                />
              </div>
            </div>
          </div>

          {/* Condiciones Comerciales */}
          <div className="form-section">
            <h3>Condiciones Comerciales</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pf-condiciones-pago">Condiciones de Pago</label>
                <select
                  id="pf-condiciones-pago"
                  value={formData.condiciones_pago}
                  onChange={(e) => handleChange('condiciones_pago', e.target.value)}
                >
                  <option value="contado">Contado</option>
                  <option value="15 dias">15 Dias</option>
                  <option value="30 dias">30 Dias</option>
                  <option value="60 dias">60 Dias</option>
                  <option value="90 dias">90 Dias</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pf-dias-credito">Dias de Credito</label>
                <input
                  id="pf-dias-credito"
                  type="number"
                  value={formData.dias_credito}
                  onChange={(e) => handleChange('dias_credito', e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="pf-descuento">Descuento Pronto Pago (%)</label>
                <input
                  id="pf-descuento"
                  type="number"
                  value={formData.descuento_pronto_pago}
                  onChange={(e) => handleChange('descuento_pronto_pago', e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="form-section form-section--last">
            <h3>Notas Adicionales</h3>
            <div className="form-group">
              <textarea
                value={formData.notas}
                onChange={(e) => handleChange('notas', e.target.value)}
                placeholder="Observaciones, condiciones especiales, instrucciones de entrega..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-guardar" disabled={loading}>
              <FaSave />
              {loading ? 'Guardando...' : isEditing ? 'Actualizar Proveedor' : 'Crear Proveedor'}
            </button>
            <button type="button" className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProveedorFormModal;
